import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon, bellIcon, clipboardIcon } from '@progress/kendo-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignal } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from '@progress/kendo-react-tooltip';
import AssetMetricsReadDto from "../../../../Models/Dashboard/AssetMetricsReadDto";
import AssetImageReadDto from "../../../../Models/Assets/AssetImageReadDto";
import { UseQueryResult } from "react-query";
import { ReactNode } from "react";
import { getStatusColor, STATUS_COLORS } from "../../Utilities/statusColorUtils";

/** A centered status dot cell (compliance / connectivity / alerts) with a tooltip title. */
const StatusDotCell = ({ color, title }: { color: string; title: string }) => (
  <td>
    <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
      <div className="flex items-center justify-center h-full w-full" style={{ cursor: 'pointer' }} title={title}>
        <SvgIcon icon={circleIcon} style={{ color }} size="xlarge" />
      </div>
    </Tooltip>
  </td>
);

/** A metric column header: icon + label with a legend tooltip. */
const MetricHeader = ({ icon, label, tooltipContent }: { icon: ReactNode; label: string; tooltipContent: () => JSX.Element }) => (
  <Tooltip anchorElement="target" position="bottom" parentTitle={true} content={tooltipContent}>
    <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }} title=" ">
      {icon}
      <span className="ms-2">{label}</span>
    </span>
  </Tooltip>
);

interface Props {
  assetsMetricsData: AssetMetricsReadDto[];
  onAssetSelect: (asset: AssetMetricsReadDto) => void;
  imagesQueries: UseQueryResult<AssetImageReadDto>[];
}

export default function AssetsDashboardTableView({ assetsMetricsData, onAssetSelect, imagesQueries }: Props) {
  const localizationService = useLocalization();

  // Custom cell components
  const NameCell = (props: any) => {
    return (
      <td>
        <span 
          className="text-primary cursor-pointer underline "
          style={{color: '#007bff'}}
          onClick={() => onAssetSelect(props.dataItem)}
        >
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  // Custom header components
  const ComplianceTooltipContent = () => (
    <div className="p-2">
      <div>{localizationService.toLanguageString('custom.complianceTooltip', enMessages.custom.complianceTooltip)}</div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.critical} />
        <span>{localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.warning} />
        <span>{localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.ok} />
        <span>{localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.unknown} />
        <span>{localizationService.toLanguageString("custom.unknown", enMessages.custom.unknown)}</span>
      </div>
    </div>
  );

  const ConnectivityTooltipContent = () => (
    <div className="p-2">
      <div>{localizationService.toLanguageString('custom.connectivityTooltip', enMessages.custom.connectivityTooltip)}</div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.ok} />
        <span>{localizationService.toLanguageString("custom.online", enMessages.custom.online)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.critical} />
        <span>{localizationService.toLanguageString("custom.offline", enMessages.custom.offline)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.warning} />
        <span>{localizationService.toLanguageString("custom.partialConnected", enMessages.custom.partialConnected)}</span>
      </div>
    </div>
  );

  const ComplianceHeader = () => (
    <MetricHeader
      icon={<SvgIcon icon={clipboardIcon} size="xlarge" style={{ verticalAlign: 'middle' }} />}
      label={localizationService.toLanguageString('custom.compliance', enMessages.custom.compliance)}
      tooltipContent={ComplianceTooltipContent}
    />
  );

  const ConnectivityHeader = () => (
    <MetricHeader
      icon={<FontAwesomeIcon icon={faSignal} size="lg" style={{ verticalAlign: 'middle' }} />}
      label={localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)}
      tooltipContent={ConnectivityTooltipContent}
    />
  );

  const AlertsTooltipContent = () => (
    <div className="p-2">
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.ok} />
        <span>{localizationService.toLanguageString('custom.noAlerts', enMessages.custom.noAlerts)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color={STATUS_COLORS.critical} />
        <span>{localizationService.toLanguageString('custom.alertsCount', enMessages.custom.alertsCount)}</span>
      </div>
    </div>
  );

  const AlertsHeader = () => (
    <MetricHeader
      icon={<SvgIcon icon={bellIcon} size="xlarge" style={{ verticalAlign: 'middle' }} />}
      label={localizationService.toLanguageString('custom.alerts', enMessages.custom.alerts)}
      tooltipContent={AlertsTooltipContent}
    />
  );

  const ComplianceCell = (props: any) => {
    const status = props.dataItem.complianceStatus;

    // Get the localized compliance status text
    let complianceText = '';
    if (status === 'Compliant') {
      complianceText = localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant);
    } else if (status === 'NonCompliant') {
      complianceText = localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant);
    } else if (status === 'PartiallyCompliant') {
      complianceText = localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant);
    } else {
      complianceText = localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown);
    }

    return <StatusDotCell color={getStatusColor(status)} title={complianceText} />;
  };

  const ConnectivityCell = (props: any) => {
    const status = props.dataItem.connectivityStatus;

    // Get the localized connectivity status text
    let connectivityText = '';
    if (status === 'Online') {
      connectivityText = localizationService.toLanguageString('custom.online', enMessages.custom.online);
    } else if (status === 'Offline') {
      connectivityText = localizationService.toLanguageString('custom.oneOrMoreDevicesAreOffline', enMessages.custom.oneOrMoreDevicesAreOffline);
    } else if (status === 'Partial') {
      connectivityText = localizationService.toLanguageString('custom.partialConnected', enMessages.custom.partialConnected);
    } else {
      connectivityText = localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown);
    }

    return <StatusDotCell color={getStatusColor(status)} title={connectivityText} />;
  };

  const AlertsCell = (props: any) => {
    const value = props.dataItem.alert?.value ?? 0;
    const alertText = `${value} ${localizationService.toLanguageString('custom.alerts', enMessages.custom.alerts)}`;

    return (
      <StatusDotCell color={value > 0 ? STATUS_COLORS.critical : STATUS_COLORS.ok} title={alertText} />
    );
  };

  const ImageCell = (props: any) => {
    const imageQuery = imagesQueries.find(query => 
      query.data?.id === props.dataItem.imageId
    );
    const imageData = imageQuery?.data?.data;

    return (
      <td>
        {imageData && (
          <img
            src={imageData}
            alt={props.dataItem.name}
            style={{ width: '65px', objectFit: 'contain' }}
          />
        )}
      </td>
    );
  };

  const ManufacturerCell = (props: any) => {
    return (
      <td>
        {props.dataItem.manufacturer !== '' ? props.dataItem.manufacturer : '-'}
      </td>
    );
  };

  const ModelCell = (props: any) => {
    return (
      <td>
        {props.dataItem.model !== '' ? props.dataItem.model : '-'}
      </td>
    );
  };

  return (
    <Grid
      data={assetsMetricsData}
      className="all-white-rows"
      style={{
        // Set a max height to the table to prevent it from overflowing
        maxHeight: 'calc(100vh - 196px)',
        borderWidth: '2px',
        overflow: 'auto'
      }}
      size="small"
      sortable={false}
    >
      <GridColumn 
        field="" 
        title={' '}
        cell={ImageCell}
        width={100}
      />
      <GridColumn 
        field="name" 
        title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
        cell={NameCell}
      />
      <GridColumn 
        field="location" 
        title={localizationService.toLanguageString('custom.location', enMessages.custom.location)}
      />
      <GridColumn 
        field="manufacturer" 
        title={localizationService.toLanguageString('custom.manufacturer', enMessages.custom.manufacturer)}
        cells={{data: ManufacturerCell}}
      />
      <GridColumn 
        field="model" 
        title={localizationService.toLanguageString('custom.model', enMessages.custom.model)}
        cells={{data: ModelCell}}
      />
      <GridColumn 
        field="complianceStatus" 
        title=""
        cell={ComplianceCell}
        headerCell={ComplianceHeader}
      />
      <GridColumn 
        field="connectivityStatus" 
        title=""
        cell={ConnectivityCell}
        headerCell={ConnectivityHeader}
      />
      <GridColumn 
        field="alerts" 
        title=""
        cell={AlertsCell}
        headerCell={AlertsHeader}
      />
    </Grid>
  );
}